import { ChangeDetectionStrategy, Component, computed, inject, input, isStandalone, signal, WritableSignal } from '@angular/core';
import { FaqItem } from '../../models';
import { DOCUMENT, NgClass } from '@angular/common';
import { MatAccordion, MatExpansionModule } from '@angular/material/expansion';
import { CookiePreferences, StripEmbedPipe } from '@teamfoster/sdk/cookie-ngrx';
import { ResizeHtmlImagesPipe } from '@teamfoster/sdk/image';
import { SafePipe, StripHtmlPipe } from '@teamfoster/sdk/text-utility';
import { JsonLdComponent } from 'src/app/seo/components/json-ld/json-ld.component';
import { FromDictionaryPipe } from '@teamfoster/sdk/dictionary-ngrx';

@Component({
  selector: 'app-faq-section',
  imports: [StripEmbedPipe, ResizeHtmlImagesPipe, SafePipe, MatAccordion, MatExpansionModule, NgClass, JsonLdComponent, StripHtmlPipe],
  templateUrl: './faq-section.component.html',
  styleUrl: './faq-section.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FaqSectionComponent {
  title = input<string>();
  subject = input<string>();
  intro = input<string>();
  group = input<{ [key: string]: FaqItem[] }>({});
  cookiePreferences = input<CookiePreferences>();

  stripHtmlPipe = new StripHtmlPipe();
  private dict = new FromDictionaryPipe();
  document = inject(DOCUMENT);

  collectionTitle = computed(() => {
    if (this.title()) {
      return this.title();
    }
    const fallBackTitle = this.dict.transform('collectie-faq-titel');
    const rawColTitle = this.subject();
    const colTitle = rawColTitle ? rawColTitle.slice(0, 1).toLowerCase() + rawColTitle.slice(1) : rawColTitle; // lowercase first letter
    return colTitle ? this.dict.transform('collectie-faq-titel-prefix') + ' ' + colTitle : fallBackTitle;
  });

  scheme = computed(() => {
    return {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      'name': this.title(),
      'url': this.document.location.href,
      'description': this.subject() ? `Frequently Asked Questions about ${this.subject()}` : this.title(),
      'mainEntity': this.getKeys(this.group()).flatMap(key => {
        const faqItems = this.group()[key] || [];
        return faqItems.map(faq => ({
          '@type': 'Question',
          'name': faq.question,
          'acceptedAnswer': {
            '@type': 'Answer',
            'text': this.stripHtmlPipe.transform(faq.answer) || '',
          },
        }));
      }),
    };
  });

  isGrid$ = computed(() => {
    return this.getKeys(this.group()).length > 1;
  });

  openPanels: WritableSignal<any> = signal(() => {
    return Object.keys(this.group()).map(key => {
      return { [key]: null };
    });
  });

  getKeys(obj: { [key: string]: any }) {
    return Object.keys(obj);
  }

  openItem(key: string, index: number) {
    this.openPanels.update(state => ({ ...state, [key]: index }));
  }
}
